import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";

import { Button } from "@/ui/button";
import { Input } from "@/ui/input";
import { Label } from "@/ui/label";
import { Alert, AlertDescription } from "@/ui/alert";
import { Textarea } from "@/ui/textarea";
import { AlertCircle } from "lucide-react";

import { buildNotification, GenerateNotification } from "smart-react";
import {
  EVENTS_DATA_TYPES,
  NOTIFICATION_TYPES,
} from "../../../../../constants/eventDataTypes";
import { requestCreateOrganization } from "../../../../Admin/Organization/Services/OrganizationService";
import { SKIP_TENANT_REQUEST } from "../../../../../constants/applicationConstants";

type TenantFormValues = {
  orgName: string;
  emailDomain: string;
  description: string;
};

type TenantFormProps = {
  setIsLoader: (value: boolean) => void;
};

const requiredMessage = "Please enter a value.";

const TenantForm: React.FC<TenantFormProps> = ({ setIsLoader }) => {
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = React.useState<string>("");

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<TenantFormValues>({
    defaultValues: {
      orgName: "",
      emailDomain: "",
      description: "",
    },
    mode: "onBlur",
  });

  const cancelRequest = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    try {
      localStorage.setItem(SKIP_TENANT_REQUEST, "true");
    } catch {
      // ignore
    }
    navigate("/");
  };

  const onSubmit = async (values: TenantFormValues) => {
    setIsLoader(true);
    setErrorMessage("");

    const data = {
      OrganizationName: values.orgName,
      Domains: values.emailDomain,
      OrganizationDescription: values.description,
      RequestedSource: "SmartApps",
    };

    try {
      const response = await requestCreateOrganization({
        data,
        // Keep optional args explicit for TS (service expects these keys)
        moduleName: "TenantRequest",
        isServiceWorker: false,
        listController: undefined,
      });
      if (!response?.IsSuccess) {
        setErrorMessage(response?.Message ?? "Request failed.");
        return;
      }

      try {
        localStorage.setItem(SKIP_TENANT_REQUEST, "true");
      } catch {
        // ignore
      }

      GenerateNotification(
        buildNotification({
          title: "Successfully Sent",
          description: "New Organization Request has been successfully sent",
          style: "success",
        }),
        NOTIFICATION_TYPES.APP,
        EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
      );

      navigate("/");
    } catch (e) {
      setErrorMessage("Something went wrong. Please try again.");
      // eslint-disable-next-line no-console
      console.log(e);
    } finally {
      setIsLoader(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      {!!errorMessage && (
        <Alert variant="destructive" className="p-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <AlertDescription className="text-sm">
              {errorMessage}
            </AlertDescription>
          </div>
        </Alert>
      )}

      <div className="space-y-2">
        <Label htmlFor="orgName">
          Organization Name <span className="text-destructive">*</span>
        </Label>
        <Input
          id="orgName"
          {...register("orgName", { required: requiredMessage })}
          aria-invalid={!!errors.orgName}
          className="bg-white dark:bg-input"
        />
        {errors.orgName?.message && (
          <p className="text-xs text-destructive">{errors.orgName.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="emailDomain">
          Email Domain <span className="text-destructive">*</span>
        </Label>
        <Input
          id="emailDomain"
          {...register("emailDomain", { required: requiredMessage })}
          aria-invalid={!!errors.emailDomain}
          className="bg-white dark:bg-input"
        />
        {errors.emailDomain?.message && (
          <p className="text-xs text-destructive">
            {errors.emailDomain.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">
          Description <span className="text-destructive">*</span>
        </Label>
        <Textarea
          id="description"
          rows={4}
          {...register("description", { required: requiredMessage })}
          aria-invalid={!!errors.description}
          className="bg-white dark:bg-input"
        />
        {errors.description?.message && (
          <p className="text-xs text-destructive">
            {errors.description.message}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={cancelRequest}
          className="bg-muted/40 hover:bg-muted/60 dark:bg-secondary/60 dark:hover:bg-secondary/80"
        >
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={isSubmitting}>
          Request
        </Button>
      </div>
    </form>
  );
};

export default TenantForm;
